import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTherapistNotes } from '@theraply/api-client';
import { decryptNote } from '@theraply/crypto';
import { base64ToBytes } from '@theraply/crypto';
import {
  DocumentTextIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { Input, Spinner } from '@theraply/ui';
import clsx from 'clsx';

interface NotesListProps {
  onSelectNote?: (bookingId: number) => void;
}

export function NotesList({ onSelectNote }: NotesListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const { data, isLoading, error } = useTherapistNotes();

  // State for decrypted notes
  const [decryptedNotes, setDecryptedNotes] = useState<any[]>([]);

  // Decrypt notes when data changes
  useEffect(() => {
    if (!data?.notes) {
      setDecryptedNotes([]);
      return;
    }

    const masterKeyBase64 = sessionStorage.getItem('therapist_master_key');
    if (!masterKeyBase64) {
      setDecryptedNotes([]);
      return;
    }

    const masterKey = base64ToBytes(masterKeyBase64);

    // Decrypt all notes asynchronously
    const decryptAll = async () => {
      const decrypted = await Promise.all(
        data.notes.map(async (note) => {
          try {
            const plaintext = await decryptNote(
              {
                ciphertext: note.encryptedContent,
                iv: note.iv,
                version: note.version,
              },
              masterKey
            );
            return { ...note, plaintext };
          } catch (error) {
            console.error('Failed to decrypt note preview:', error);
            return { ...note, plaintext: '[Decryption failed]' };
          }
        })
      );
      setDecryptedNotes(decrypted);
    };

    decryptAll();
  }, [data]);

  // Filter notes by search query
  const filteredNotes = useMemo(() => {
    if (!searchQuery.trim()) return decryptedNotes;

    const query = searchQuery.toLowerCase();
    return decryptedNotes.filter((note) => {
      const consumer = note.booking.consumer;
      const fullName = `${consumer.firstName} ${consumer.lastName}`.toLowerCase();
      const email = consumer.email.toLowerCase();
      const content = stripHtml(note.plaintext || '').toLowerCase();

      return (
        fullName.includes(query) ||
        email.includes(query) ||
        content.includes(query)
      );
    });
  }, [decryptedNotes, searchQuery]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading notes</h3>
        <p className="mt-1 text-sm text-gray-500">{error.message}</p>
      </div>
    );
  }

  if (!data || data.notes.length === 0) {
    return (
      <div className="text-center py-12">
        <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No session notes</h3>
        <p className="mt-1 text-sm text-gray-500">
          Session notes will appear here after your therapy sessions.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <Input
          type="text"
          placeholder="Search by client name or note content..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Notes List */}
      {filteredNotes.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-sm text-gray-500">No notes match your search.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredNotes.map((note) => (
            <NoteCard
              key={note.id}
              note={note}
              onSelect={() => onSelectNote?.(note.bookingId)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface NoteCardProps {
  note: any;
  onSelect: () => void;
}

function NoteCard({ note, onSelect }: NoteCardProps) {
  const { booking } = note;
  const consumer = booking.consumer;
  const preview = getPreview(note.plaintext || '');

  return (
    <button
      onClick={onSelect}
      className={clsx(
        'w-full text-left p-4 rounded-lg border border-gray-200',
        'hover:border-blue-500 hover:shadow-md transition-all',
        'bg-white'
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Client Info */}
          <div className="flex items-center gap-2 mb-2">
            <UserIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <p className="text-sm font-medium text-gray-900">
              {consumer.firstName} {consumer.lastName}
            </p>
            <span className="text-xs text-gray-500">({consumer.email})</span>
          </div>

          {/* Session Info */}
          <div className="flex items-center gap-2 mb-2">
            <CalendarIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <p className="text-xs text-gray-600">
              {formatDate(booking.slotDate)} at {formatTime(booking.slotTime)}
            </p>
            <span
              className={clsx(
                'px-2 py-0.5 rounded text-xs font-medium',
                booking.sessionType === 'INTAKE'
                  ? 'bg-blue-100 text-blue-800'
                  : booking.sessionType === 'CRISIS'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-green-100 text-green-800'
              )}
            >
              {booking.sessionType}
            </span>
          </div>

          {/* Note Preview */}
          <p className="text-sm text-gray-600 line-clamp-2">{preview}</p>
        </div>

        {/* Last Updated */}
        <div className="flex-shrink-0 text-xs text-gray-500">
          {formatDate(note.updatedAt)}
        </div>
      </div>
    </button>
  );
}

// Helper functions

/**
 * Safely strip HTML tags from content using DOMParser
 * This prevents XSS vulnerabilities by properly parsing HTML
 */
function stripHtml(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

function getPreview(html: string, maxLength: number = 150): string {
  const text = stripHtml(html);
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTime(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}
